import { Slot } from '@radix-ui/react-slot'
import { cva, type VariantProps } from 'class-variance-authority'
import * as React from 'react'

import { cn } from '@/theme/utils'

const buttonGradientVariants = cva(
  cn(
    'cursor-pointer inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md transition-colors focus-visible:outline-hidden focus-visible:ring-1 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:shrink-0',
    '[&_div]:rounded-[inherit]',
  ),
  {
    variants: {
      variant: {
        primary: cn(
          'bg-[radial-gradient(100%_453.25%_at_0%_100%,#F7931A_0%,#F5D7AA_100%)]',
          '[box-shadow:0px_0px_15px_0px_#F7931A59]',
          'text-[#0D0E12]',
        ),
        secondary: cn(
          'relative',
          'bg-[linear-gradient(52.09deg,rgba(255,255,255,0)_0%,rgba(247,147,26,0.5)_90.23%)] p-1',
          '[&_.bg-wrp]:absolute-center',
          '[&_.bg-wrp]:size-[95%]',
          '[&_.bg-wrp]:bg-[radial-gradient(97.28%_149.03%_at_82.63%_-49.03%,#323439_0%,#0D0E12_100%)]',
          '[&_.content-wrp]:gradient-text',
        ),
        outline: cn(
          'relative',
          'after:border-after-gradient after:rounded-[inherit]',
          '[box-shadow:_0px_0px_15px_0px_#F7931A59]',
          '[&_.content-wrp]:gradient-text',
        ),
        /*
        {
          content: '';
          position: absolute;
          top: 0;
          left: 0;
          width: 100%;
          height: 100%;
          border: 2px solid transparent;
          background: linear-gradient(45deg, #f7931a 0%, #f5d7aa 100%) border-box;
          mask:linear-gradient(#000 0 0) padding-box,linear-gradient(#000 0 0);
          mask-composite: exclude;
        }
        */
        ghost: '',
      },
      size: {
        sm: 'h-8 px-3 text-xs',
        md: 'h-10.5 px-4 py-2 typography-m3-body-large font-semibold',
        lg: 'h-12.5 px-8 typography-m3-body-large font-semibold',
        icon: 'h-9 w-9',
      },
      radius: {
        none: 'rounded-none',
        sm: 'rounded-sm',
        default: 'rounded-md',
        lg: 'rounded-lg',
        full: 'rounded-full',
      },
    },
    defaultVariants: {
      variant: 'primary',
      size: 'lg',
      radius: 'full',
    },
  },
)

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonGradientVariants> {
  asChild?: boolean
}

const UiButtonGradient = React.forwardRef<HTMLButtonElement, ButtonProps>(
  (
    { className, variant, size, radius, asChild = false, children, ...props },
    ref,
  ) => {
    const Comp = asChild ? Slot : 'button'
    return (
      <Comp
        className={cn(
          buttonGradientVariants({ variant, size, radius, className }),
        )}
        ref={ref}
        {...props}
      >
        <div className='bg-wrp'>
          <div className='content-wrp flex size-full items-center justify-center'>
            {children}
          </div>
        </div>
      </Comp>
    )
  },
)
UiButtonGradient.displayName = 'UiButtonGradient'

export { buttonGradientVariants, UiButtonGradient }
